import React, { useEffect, useState } from 'react'
import { Button, Stack } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import Modal from '../common/modal'
import Lineinput from '../common/lineinput'
import { Item } from '../common/snippets'
import { useTranslation } from '../../i18n/I18nProvider'
import { useAtom } from 'jotai'
import { alertAtom } from '../common/jotaiState/globalStateAtoms'
import { sendMtplMasterOdp, validateMtplMasterOdp } from '../common/jotaiState/commonStateAtoms'
import { decryptText } from '../common/loginDecode'

export const MTPL_MASTER_ODP_SESSION_KEY = 'mtplMasterOdpValidated'
export const ODP_LOGIN_PARAMS_KEY = 'odpLoginParams'

export const isMtplMasterOdpValidated = () => sessionStorage.getItem(MTPL_MASTER_ODP_SESSION_KEY) === '1'

export const setMtplMasterOdpValidated = () => {
  sessionStorage.setItem(MTPL_MASTER_ODP_SESSION_KEY, '1')
}

export const getOdpLoginParams = () => {
  try {
    const stored = sessionStorage.getItem(ODP_LOGIN_PARAMS_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed?.LoginID && parsed?.Code) return parsed
    }
  } catch (e) {
    console.log(e)
  }

  const auth = sessionStorage.getItem('authentication')
  if (auth) {
    const decrypted = decryptText(auth)
    if (decrypted && decrypted !== 'ErrorInDecrypting') {
      const parts = decrypted.split('~')
      if (parts.length >= 4) {
        const [, , Code, LoginID] = parts
        if (LoginID && Code) return { LoginID, Code }
      }
    }
  }
  return { LoginID: '', Code: '' }
}

const MtplOdpValidationModal = ({ loginData, onValidated, onClose }) => {
  const { translations } = useTranslation()
  const mtpl = translations?.mtplMaster || {}
  const tr = (key, fallback) => mtpl?.[key] || fallback
  const [, setAlert] = useAtom(alertAtom)
  const [odp, setOdp] = useState('')
  const [error, setError] = useState(false)
  const [isClicked, setIsClicked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('')
  const { LoginID, Code } = getOdpLoginParams()
  const sendPayload = {
    LoginID,
    Code,
    check: 0,
  }

  const sendOdp = () => {
    sendMtplMasterOdp(sendPayload).then((res) => {
      if (!res?.status) {
        setAlert({ show: true, msg: res?.Message || tr('failedToSendOdp', 'Failed to send ODP'), nextId: '' })
      }
    })
  }

  useEffect(() => {
    sendOdp()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleResend = () => {
    setIsClicked(true)
    setError(false)
    setErrorMessage('')
    setOdp('')
    sendOdp()
    setTimeout(() => setIsClicked(false), 100);
  }

  const handleValidate = () => {
    if (!odp) {
      setError(true)
      setErrorMessage('')
      return
    }
    validateMtplMasterOdp({
      intUserID: loginData?.intUserID,
      intBranchID: loginData?.intBranchID,
      intMTPLODP: odp,
    }).then((res) => {
      if (res?.status) {
        setError(false)
        setErrorMessage('')
        setMtplMasterOdpValidated()
        onValidated?.()
      } else {
        setError(true)
        setErrorMessage(res?.Message || '')
        setAlert({ show: true, msg: res?.Message || tr('wrongOtpEntered', 'Wrong OTP Entered'), nextId: '' })
      }
    })
  }

  return (
    <Modal
      headTxt={tr('odpValidation', 'ODP Validation')}
      width={'30%'}
      updateModal={onClose}
      closeId={'closeButton'}
      modalClass={'booking-popup'}
      modalStyle={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 500 }}
      modalHeaderStyle={{ textTransform: 'none' }}
    >
      <Stack style={{ display: 'flex', flexDirection: 'column', padding: '16px 16px', gap: '16px', justifyContent: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: 'Roboto', fontSize: '14px', fontWeight: '500', color: '#444444' }}>
            {tr('odpSent', 'ODP Sent')}
          </div>
          <div
            style={{
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontWeight: '500',
              color: isClicked ? '#0056a6' : '#0086FF',
              textDecoration: 'underline',
              cursor: 'pointer',
              transform: isClicked ? 'scale(0.95)' : 'scale(1)',
              opacity: isClicked ? 0.7 : 1,
              transition: 'all 0.1s ease'
            }}
            onClick={handleResend}
          >
            {tr('resendOdp', 'Resend ODP')}
          </div>
        </div>
        <Item className="input--wrapper">
          <Lineinput
            id="mtplOdpInput"
            autoFocus
            sx={{ width: '100%', fontSize: '14px', fontFamily: 'Roboto', fontWeight: '400', borderColor: error ? 'red' : '' }}
            label={tr('enterOtp', 'Enter OTP')}
            value={odp}
            onChange={(e) => {
              setOdp(e.target.value)
              setError(false)
              setErrorMessage('')
            }}
            isError={error}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleValidate()
              }
            }}
          />
        </Item>
        {error && (
          <div style={{ display: 'flex', alignItems: 'center', color: '#F05924', fontSize: '14px', fontWeight: 400 }}>
            <InfoIcon style={{ fontSize: '15px', marginRight: '4px' }} />
            {errorMessage || tr('wrongOtpEntered', 'Wrong OTP Entered')}
          </div>
        )}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Button
            id="mtplOdpValidateBtn"
            style={{ height: '40px', width: '100%' }}
            className="btn add-btn"
            variant="contained"
            color="error"
            onClick={handleValidate}
          >
            {tr('validate', 'VALIDATE')}
          </Button>
        </div>
      </Stack>
    </Modal>
  )
}

export default MtplOdpValidationModal
